import clsx from 'clsx'
import { cloneElement, isValidElement, type ReactElement, type ReactNode } from 'react'

import styles from './FilterChips.module.scss'

function renderChipIcon(icon: ReactNode, active: boolean) {
  if (!icon) return null

  const rendered =
    active && isValidElement(icon)
      ? cloneElement(icon as ReactElement<{ style?: React.CSSProperties }>, {
          style: {
            ...(icon.props.style ?? {}),
            color: '#fff'
          }
        })
      : icon

  return <span className={styles.chipIcon}>{rendered}</span>
}

export type FilterChipOption<T extends string = string> = {
  id: T
  label: string
  icon?: ReactNode
}

type FilterChipsProps<T extends string> = {
  options: FilterChipOption<T>[]
  value: T
  onChange: (id: T) => void
  className?: string
  'aria-label'?: string
}

export const FilterChips = <T extends string>({
  options,
  value,
  onChange,
  className,
  'aria-label': ariaLabel = 'Filtrar itens'
}: FilterChipsProps<T>) => (
  <div
    className={clsx(styles.row, className)}
    role="group"
    aria-label={ariaLabel}
  >
    {options.map(option => (
      <button
        key={option.id}
        type="button"
        className={clsx(
          styles.chip,
          value === option.id && styles.chipActive
        )}
        onClick={() => onChange(option.id)}
        aria-pressed={value === option.id}
      >
        {renderChipIcon(option.icon, value === option.id)}
        {option.label}
      </button>
    ))}
  </div>
)

FilterChips.displayName = 'FilterChips'
